import React from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, Loader2 } from "lucide-react";

/**
 * Modal QR-code scanner using the device's camera.
 * Opens a fullscreen overlay with the camera preview.
 * Props: open: boolean, onClose: ()=>void, onScan: (code: string)=>void
 */
export default function CameraQRScanner({ open, onClose, onScan }) {
  const [error, setError] = React.useState(null);
  const [starting, setStarting] = React.useState(true);
  const containerId = React.useId().replace(/[^a-zA-Z0-9]/g, "_");
  const scannerRef = React.useRef(null);
  const onScanRef = React.useRef(onScan);
  onScanRef.current = onScan;

  React.useEffect(() => {
    if (!open) return;
    let alive = true;
    setError(null);
    setStarting(true);
    (async () => {
      try {
        const html5 = new Html5Qrcode(containerId);
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decodedText) => {
            if (!alive) return;
            try { html5.pause(true); } catch { /* ignore */ }
            onScanRef.current?.(decodedText);
          },
          () => { /* ignore per-frame errors */ }
        );
        if (alive) setStarting(false);
      } catch (e) {
        if (alive) setError(e?.message || "Kunne ikke starte kameraet");
        if (alive) setStarting(false);
      }
    })();
    return () => {
      alive = false;
      try {
        scannerRef.current?.stop().then(() => scannerRef.current?.clear());
      } catch { /* ignore */ }
      scannerRef.current = null;
    };
  }, [open, containerId]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="camera-scanner-modal">
      <div className="flex items-center justify-between p-4 text-white">
        <div className="flex items-center gap-2">
          <Camera size={18} />
          <span className="font-display font-bold">Scan QR / barcode</span>
        </div>
        <button onClick={onClose} className="text-white/80 hover:text-white p-2" data-testid="camera-scanner-close">
          <X size={22} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center relative">
        <div id={containerId} className="w-full max-w-md" />
        {starting && (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <Loader2 className="animate-spin" size={28} />
          </div>
        )}
        {error && (
          <div className="absolute inset-x-4 bottom-20 bg-red-600 text-white p-4 rounded text-sm" data-testid="camera-scanner-error">
            <strong>Fejl:</strong> {error}
            <p className="text-xs mt-2 opacity-90">Tjek at browseren har tilladelse til at bruge kameraet.</p>
          </div>
        )}
      </div>
      <div className="p-5 text-center text-white/70 text-xs">
        Hold pakkens QR-kode foran kameraet — koden læses automatisk.
      </div>
    </div>
  );
}
